import { useState } from "react";
import { Link } from "react-router-dom";
import { FullBrandLogo } from "@/components/Brand";
import { ShieldCheck, Menu, X, ChevronDown, ChevronRight, Phone, User } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SiteSearchOverlay from "@/components/SiteSearchOverlay";
import AIAvailabilityChip from "@/components/AIAvailabilityChip";
import { useT } from "@/lib/i18n";

/**
 * PublicHeader — marketing / public pages header.
 *
 * BoldTrail-inspired layout (Feb 2026):
 *   • Optional slim top strip with sales phone + Login (visible on mobile).
 *   • Main bar with bigger bold logo + desktop nav + hamburger card on
 *     mobile that reveals a full-width dropdown drawer.
 *   • Colors untouched — reuses the existing emerald / slate palette.
 */
export default function PublicHeader({ light = false, showTopStrip = false }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  const navLinks = [
    { href: "#features",    label: t("header.features", "Features") },
    { href: "#product",     label: t("header.product",  "Product") },
    { href: "#tenants",     label: t("header.for_tenants", "For Tenants") },
    { to:   "/tools",       label: t("header.tools",    "Tools"),        testid: "header-tools-link" },
    { to:   "/insights",    label: t("header.insights", "Insights"),     testid: "header-insights-link" },
    { to:   "/pricing",     label: t("header.pricing",  "Pricing"),      testid: "header-pricing-link" },
    { to:   "/partnership", label: t("header.partnership", "Refer & earn"), testid: "header-partnership-link" },
  ];

  return (
    <header
      data-testid="public-header"
      className={`${light ? "bg-white/80 border-b border-slate-200 backdrop-blur-xl" : "bg-transparent"} sticky top-0 z-40`}
    >
      {/* Slim top strip — sales contact left, portal login right (BoldTrail-style). */}
      {showTopStrip && (
        <div className={`w-full ${light ? "bg-slate-900 text-white" : "bg-black/25 text-white"}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-5 md:px-8 h-9 sm:h-10 flex items-center justify-between text-xs sm:text-sm">
            <a
              href="mailto:contact@centralrentals.ca?subject=Sales%20enquiry"
              data-testid="header-call-sales"
              aria-label="Contact Sales"
              title="Contact Sales — contact@centralrentals.ca"
              className="inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-white/10 transition-colors"
            >
              <Phone className="w-4 h-4" />
            </a>
            <div className="flex items-center gap-3">
              <Link
                to="/login/landlord"
                data-testid="header-topstrip-login"
                className="inline-flex items-center gap-1.5 font-semibold hover:text-emerald-300 transition-colors"
              >
                <User className="w-3.5 h-3.5" /> {t("header.login", "Login")}
                <ChevronDown className="w-3.5 h-3.5 opacity-70" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Main bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-5 md:px-8 min-h-20 sm:min-h-24 md:min-h-28 py-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-4">
        <div className="flex items-center gap-2.5 min-w-0 flex-shrink-0">
          <FullBrandLogo height={44}/>
          <span
            data-testid="secure-site-indicator"
            title="Verified secure connection — centralrentals.ca"
            className={`hidden 2xl:inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide flex-none ${light ? "bg-green-50 text-green-700 border border-green-200" : "bg-emerald-400/15 text-emerald-300 border border-emerald-400/30"}`}
          >
            <ShieldCheck className="w-3.5 h-3.5"/> {t("header.secure", "Secure")}
          </span>
        </div>

        {/* Desktop nav — lg+ only so it never collides with the logo */}
        <nav className="hidden lg:flex order-last w-full flex-wrap justify-center items-center gap-x-5 gap-y-3 text-sm font-medium">
          {navLinks.map((l) => (
            l.to ? (
              <Link key={l.label} to={l.to} data-testid={l.testid}
                className={light ? "text-slate-700 hover:text-slate-950" : "text-white/80 hover:text-white"}>
                {l.label}
              </Link>
            ) : (
              <a key={l.label} href={l.href}
                className={light ? "text-slate-700 hover:text-slate-950" : "text-white/80 hover:text-white"}>
                {l.label}
              </a>
            )
          ))}
        </nav>

        {/* Desktop auth + mobile hamburger card */}
        <div className="flex items-center justify-end gap-2 flex-none max-w-full">
          <SiteSearchOverlay />
          <a
            href="tel:+17804052498"
            data-testid="header-phone-icon"
            aria-label="Call Central Rentals — 780-405-2498"
            title="Call Central Rentals · 780-405-2498"
            className={`hidden xl:inline-flex items-center gap-2 px-3 h-9 rounded-full text-sm font-semibold transition-colors ${light ? "bg-slate-100 hover:bg-slate-200 text-slate-800" : "bg-white/10 hover:bg-white/20 text-white"}`}
          >
            <Phone className="w-4 h-4" />
            <span className="tabular-nums">780-405-2498</span>
          </a>
          <div className="mr-1 hidden md:block"><LanguageSwitcher/></div>
          <div className="mr-1 hidden lg:inline-flex"><AIAvailabilityChip compact /></div>
          <Link
            to="/login/landlord"
            data-testid="header-login-landlord"
            className={`hidden sm:inline-flex px-3 py-2 rounded-md text-sm font-medium ${light ? "text-slate-900 hover:bg-slate-100" : "text-white hover:bg-white/10"}`}
          >
            {t("header.sign_in", "Sign in")}
          </Link>
          <Link
            to="/signup"
            data-testid="header-signup-link"
            className="hidden sm:inline-flex items-center px-4 sm:px-5 py-2.5 rounded-full text-sm font-bold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-md whitespace-nowrap"
          >
            {t("header.sign_up", "Sign up")}
          </Link>

          {/* Mobile hamburger — BoldTrail-style white rounded card */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="public-mobile-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            className="lg:hidden hamburger-card"
          >
            {open ? <X className="w-5 h-5"/> : <Menu className="w-5 h-5"/>}
          </button>
        </div>
      </div>

      {/* Mobile drawer — shown at anything below lg since desktop nav is lg:flex */}
      {open && (
        <div
          data-testid="public-mobile-drawer"
          className="lg:hidden absolute inset-x-0 top-full bg-white shadow-xl border-t border-slate-200 rounded-b-2xl overflow-hidden"
        >
          <div className="max-h-[80vh] overflow-y-auto">
            {navLinks.map((l) => (
              l.to ? (
                <Link key={l.label} to={l.to} onClick={close} className="mobile-nav-row">
                  <span>{l.label}</span>
                  <ChevronRight className="w-5 h-5 opacity-50"/>
                </Link>
              ) : (
                <a key={l.label} href={l.href} onClick={close} className="mobile-nav-row">
                  <span>{l.label}</span>
                  <ChevronRight className="w-5 h-5 opacity-50"/>
                </a>
              )
            ))}
            <Link to="/login/tenant" onClick={close} className="mobile-nav-row" data-testid="public-mobile-tenant-login">
              <span>{t("header.tenant_login", "Tenant login")}</span>
              <ChevronRight className="w-5 h-5 opacity-50"/>
            </Link>
            <Link to="/login/landlord" onClick={close} className="mobile-nav-row" data-testid="public-mobile-landlord-login">
              <span>{t("header.landlord_login", "Landlord login")}</span>
              <ChevronRight className="w-5 h-5 opacity-50"/>
            </Link>
            <div className="p-4 sm:p-5 flex items-center gap-3 border-t border-slate-200">
              <LanguageSwitcher/>
              <Link
                to="/signup"
                onClick={close}
                data-testid="public-mobile-signup"
                className="ml-auto inline-flex items-center px-5 py-3 rounded-full text-sm font-bold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-md"
              >
                {t("header.sign_up", "Sign up")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
